import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { Pill } from '@blk/daisy/combinations/filters';
import { DzSearchFilterEvent } from '@blk/daisy/combinations/search/search.model';
import { DzCheckboxChange } from '@blk/daisy/elements/selection-controls';
import { TableHeader } from '@blk/daisy/modules/table';
import { V1ExternalAccountInfo } from '@blk/portfolio-security-server/accountInfo';
import { APP_STORE_CONFIG_TOKEN } from '@ddt-web/config';
import { InjectConfig, PaConfigKey } from '@ddt-web/config-layer';
import { AppStoreConfig } from '@ddt-web/data-service';
import { PartnerPlatformControlsList, PartnerSearchKey, SharedApplicationConstants } from '@ddt-web/domain';
import { Memoize } from '@ddt-web/generic-utils';
import { ApplicationStateStoreProxyService, PartnerPlatform } from '@ddt-web/shared-stores';
import { StoreService } from '@ddt-web/store-service';
import { UrlService } from '@ddt-web/url';
import { PartnerApiError, PartnerConstants } from '@ddt-web/user-data';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { first, withLatestFrom } from 'rxjs/operators';

import { SearchResultRow } from '../../models';
import { PartnerPortfoliosDataService } from '../../services/partner-portfolios-data.service';

@UntilDestroy()
@Component({
	selector: 'ipp-portfolio-selection',
	templateUrl: './portfolio-selection.component.html',
	styleUrls: ['./portfolio-selection.component.scss']
})
export class PortfolioSelectionComponent implements OnInit {
	@InjectConfig(PaConfigKey.PARTNER_PLATFORM_CONTROLS_LIST) public readonly partnerPlatformControlsList: PartnerPlatformControlsList;
	@InjectConfig(PaConfigKey.MAX_PORTFOLIO_SELECTION_LIMIT) public readonly maxPortfolioLimit: number;

	@Output() public searchError: EventEmitter<PartnerApiError> = new EventEmitter();

	@Input() private mode: string = '';

	private searchText: string;

	public digitToWordMapping = {
		1: 'one',
		2: 'two',
		3: 'three'
	};
	public selectedPartner: PartnerPlatform;
	public selectedPortfolios: Map<string, V1ExternalAccountInfo>;
	public searchCriteria: PartnerSearchKey[];
	public showSearchOptions: boolean = true;
	public selectedSearchCriteria: PartnerSearchKey;
	public selectedPortfoliosPills: Pill[] = [];

	public searchResultRows: SearchResultRow[] = [];
	public currentlyAnalyzingAccountIds: string[] = [];
	public isSearching: boolean = false;
	public isCompareMode: boolean = false;
	public resultsFound: boolean = true;

	public maxLimit: number;

	public tableHeaders: Array<any> = [];

	private urlIds: string[] = [];

	public constructor(
		private partnerPortfolioDataService: PartnerPortfoliosDataService,
		private applicationStateStoreProxyService: ApplicationStateStoreProxyService,
		private urlService: UrlService,
		private storeService: StoreService,
		@Inject(APP_STORE_CONFIG_TOKEN) private storeConfig: AppStoreConfig
	) { }

	public ngOnInit(): void {
		this.isCompareMode = this.mode === SharedApplicationConstants.DIALOG_TABLE_MODE.COMPARE;
		this.urlIds = this.urlService.getPortfolioIds();
		this.maxLimit = this.isCompareMode ? this.maxPortfolioLimit - this.urlIds.length : this.maxPortfolioLimit;
		this.initSelectedPortfoliosAndPartner();
	}

	public onSearchClick() {
		this.isSearching = true;
		this.partnerPortfolioDataService.getPortfolioSearchList(this.selectedPartner.id, this.selectedSearchCriteria.searchCriteriaIds, this.searchText)
			.pipe(first())
			.subscribe({
				next: (searchResults) => {
					const selectedPortfolioIds = this.selectedPortfoliosPills.map(pill => pill.id);
					this.searchResultRows = searchResults.map(portfolio => {
						const partnerData = {};

						this.partnerPlatformControlsList[this.selectedPartner.id].searchResultsTableColumnDefs.forEach(column => {
							const value = column.apiDataPath.split('.').reduce((acc, key) => acc?.[key], portfolio);
							partnerData[column.id] = value;
						});

						return {
							portfolio,
							partnerData,
							isChecked: selectedPortfolioIds.includes(portfolio.id)
						};
					});
					this.resultsFound = !!this.searchResultRows.length;
					this.isSearching = false;
				},
				error: (error: PartnerApiError) => {
					console.error('An error occurred with searching accounts', error);
					this.isSearching = false;
					this.searchResultRows = [];
					this.searchError.emit(error);
				}
			});
	}

	public onSearchFilterValueChange(event: DzSearchFilterEvent) {
		this.searchText = event.searchText.trim();
	}

	public onPortfolioSelected(event: DzCheckboxChange): void {
		const selectedPortfolioRow: SearchResultRow = this.searchResultRows.find(row => row.portfolio.id === event.source.value);
		const selectedPortfolio: V1ExternalAccountInfo = selectedPortfolioRow.portfolio;

		if (this.selectedPortfolios.has(selectedPortfolio.id)) {
			this.selectedPortfolios.delete(selectedPortfolio.id);
		} else {
			this.selectedPortfolios.set(selectedPortfolio.id, selectedPortfolio);
		}
		this.applicationStateStoreProxyService.updateImportPartnerPortfoliosSelectedPortfolios(this.selectedPortfolios);
	}

	public onPillsChanged(event: { deselectAll: boolean; deselectedItem: Pill[]; selectedItems: Pill[] }): void {
		event.deselectedItem.forEach(portfolio => this.selectedPortfolios.delete(portfolio.id));
		this.applicationStateStoreProxyService.updateImportPartnerPortfoliosSelectedPortfolios(this.selectedPortfolios);
	}

	private initSelectedPortfoliosAndPartner(): void {
		const state$ = this.applicationStateStoreProxyService.getImportPartnerPortfoliosState();
		state$.pipe(
			untilDestroyed(this),
			withLatestFrom(this.storeService.selectAll(this.storeConfig.associatedPortfolios))
		).subscribe(([importPartnerPortfoliosState, associatedPortfolios]) => {
			const isPartnerChanged = this.selectedPartner?.id !== importPartnerPortfoliosState.partnerPlatform?.id;
			this.selectedPartner = importPartnerPortfoliosState.partnerPlatform;
			this.selectedPortfolios = importPartnerPortfoliosState.selectedPortfolios;
			this.selectedPortfoliosPills = Array.from(this.selectedPortfolios.values()).map(selectedPortfolio => {
				const nameSuffix = selectedPortfolio.name ? ': ' + selectedPortfolio.name : '';
				return { id: selectedPortfolio.id, name: selectedPortfolio.id + nameSuffix };
			});
			this.searchResultRows = isPartnerChanged ? [] : this.searchResultRows.map(row => {
				return { ...row, isChecked: !!this.selectedPortfoliosPills.find(pill => pill.id === row.portfolio.id) };
			});

			this.buildSearchCriteria(isPartnerChanged, this.selectedPartner?.id);
			this.setAccountsCurrentlyInAnalysis(associatedPortfolios);
			if (isPartnerChanged) {
				this.buildSearchTableHeaders();
			}
		});
	}

	private setAccountsCurrentlyInAnalysis(associatedPortfolios): void {
		const partnerUrlIds = this.urlIds.filter(id => id.includes(PartnerConstants.PARTNER.EXT_IDENTIFIER));
		this.currentlyAnalyzingAccountIds = partnerUrlIds.map(id => associatedPortfolios.entities[id].encrypted?.originPartnerAccountId);
	}

	private buildSearchCriteria(isPartnerChanged: boolean, selectedPartnerId: string): void {
		this.searchCriteria = this.partnerPlatformControlsList[selectedPartnerId]?.searchKeys;
		this.showSearchOptions = this.partnerPlatformControlsList[selectedPartnerId]?.searchKeys?.filter(searchKey => searchKey.enabled).length > 1;
		this.selectedSearchCriteria = !isPartnerChanged && this.selectedSearchCriteria ? this.selectedSearchCriteria : this.searchCriteria?.[0];
	}

	private buildSearchTableHeaders(): void {
		this.tableHeaders = this.partnerPlatformControlsList[this.selectedPartner.id].searchResultsTableColumnDefs.map(column =>
			new TableHeader(column.columnHeader, null, null, null, null, null, column.id)
		);
	}

	@Memoize()
	public isRowDisabled(searchResultRow: SearchResultRow): boolean {
		return (this.selectedPortfolios.size >= this.maxLimit && !this.selectedPortfolios.has(searchResultRow.portfolio.id)) ||
			(this.isCompareMode && this.currentlyAnalyzingAccountIds.includes(searchResultRow.portfolio.id));
	}
}
